import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getReservationBySpaceId, Reservation } from '../api/reservationApi';
import Logo from '../componets/Logo';
import Footer from '../componets/Footer';
import { getUnreadCountByReservations, markNotificationAsRead } from '../api/notificationApi';
import { Helmet } from 'react-helmet-async';

const ReservationList: React.FC = () => {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();

  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreadByReservations, setUnreadByReservations] = useState<{ [key: number]: number }>({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const statusKoreanMap: Record<Reservation['status'], string> = {
    PENDING: '예약 요청 | 승인 대기 중',
    APPROVED: '예약 승인됨 | 결제 대기 중',
    REJECTED: '예약 거절됨',
    PAYMENT_COMPLETED: '결제 완료 | 보관등록 가능',
    STORED: '보관 중',
    RETURNED: '반납 완료',
    CANCELED: '예약 취소됨',
    FINISHED: '예약 종료',
  };

  useEffect(() => {
    const token = localStorage.getItem('user_token');
    setIsLoggedIn(!!token);

    const loadReservations = async () => {
      setLoading(true);
      setError(null);

      try {
        // 1️⃣ 예약 목록 가져오기
        const data: Reservation[] = await getReservationBySpaceId(Number(spaceId));
        setReservations(data);

        // 2️⃣ 예약 ID 배열 만들기
        const reservationIds = data.map((r) => r.id);

        if (reservationIds.length > 0) {
          // 3️⃣ 한 번에 POST로 unread count 가져오기
          const unreadNotifications = await getUnreadCountByReservations(reservationIds);
          setUnreadByReservations(unreadNotifications);
        }
      } catch (err) {
        setError('Failed to load reservations');
      } finally {
        setLoading(false);
      }
    };

    loadReservations();
  }, [spaceId]);

  const handleReservationClick = async (reservationId: number) => {
    navigate(`/page/ReservationDetail/${reservationId}`);

    if (unreadByReservations[reservationId] > 0) {
      try {
        await markNotificationAsRead(reservationId);
        setUnreadByReservations((prev) => ({ ...prev, [reservationId]: 0 }));
      } catch (err) {
        console.error('Failed to mark as read', err);
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>예약 목록 | BIN-GO</title>
        <meta name="description" content="모든 예약 현황/목록을 관리하는 페이지입니다." />
        <meta property="og:title" content="예약 목록 | BIN-GO" />
        <meta property="og:description" content="전체 예약 리스트 관리." />
      </Helmet>
      <header className="px-4 lg:px-6 py-4">
        <Logo />
      </header>

      <div className="flex-1 px-4 lg:px-6 py-4">
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : reservations.length === 0 ? (
          <p>No reservations found.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {reservations.map((reservation) => (
              <div
                key={reservation.id}
                className="relative border rounded shadow hover:bg-gray-100 cursor-pointer overflow-hidden"
                onClick={() => handleReservationClick(reservation.id)}
              >
                {/* 사진 */}
                <div className="h-48 w-full bg-gray-200 overflow-hidden">
                  {reservation.main_image ? (
                    <img
                      src={reservation.main_image}
                      alt={reservation.depositor.nickname}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No Image
                    </div>
                  )}
                </div>

                {/* Notification */}
                {unreadByReservations[reservation.id] > 0 && (
                  <span className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
                )}

                <div className="p-4 flex flex-col">
                  <p className="font-semibold text-lg">{reservation.depositor.nickname}</p>
                  <p className="text-sm text-gray-500">보관 시작일: {reservation.start_date}</p>
                  <p className="text-sm text-gray-500">
                    상태: {statusKoreanMap[reservation.status] || reservation.status}
                  </p>
                  <p className="text-sm text-gray-500">요금: {reservation.total_price?.toLocaleString() || 'N/A'} (/일)</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ReservationList;
